import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getDownloadURL,ref, uploadBytesResumable } from 'firebase/storage';

import { ImportTarget } from '@ekhodealer/ekho-common';

import type { ImportJob } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { storage } from '@config/firebase';
import { apiClient } from '@config/server/apiClient';
import { importsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Upload CSV file to GCP storage
 * Matches the pattern used in buyer portal
 */
const uploadFileToGCP = async (file: File, servicesProviderId: string): Promise<string> => {
	const timestamp = Date.now();
	const fileName = `importJobs/${servicesProviderId}/${timestamp}-${file.name}`;
	const storageRef = ref(storage, fileName);

	// Upload file to Firebase Storage using resumable upload
	const uploadTask = uploadBytesResumable(storageRef, file);

	// Wait for upload to complete
	await uploadTask;

	// Get the download URL (Firebase Storage URL)
	const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
	return downloadURL;
};

/**
 * Import inventory from CSV file
 * First uploads to GCP, then calls the import API with the file path
 */
export const importInventory = async (
	servicesProviderId: string,
	file: File,
): Promise<ImportJob> => {
	// Step 1: Upload file to GCP storage
	const filePath = await uploadFileToGCP(file, servicesProviderId);

	// Step 2: Call import API with file path and target
	const response = await apiClient.post<ImportJob>(
		importsEndpoints.importInventory(servicesProviderId),
		{
			target: ImportTarget.INVENTORY,
			filePath,
		},
	);

	return response.data;
};

/**
 * Hook for importing inventory from CSV
 */
export const useImportInventory = () => {
	const queryClient = useQueryClient();
	const servicesProviderId = useCurrentServicesProviderId();

	return useMutation({
		mutationFn: async (file: File) => {
			if (!servicesProviderId) {
				throw new Error('Services provider ID is required');
			}

			return importInventory(servicesProviderId, file);
		},
		onSuccess: () => {
			// Invalidate inventory list to refresh the table
			queryClient.invalidateQueries({
				queryKey: queries.inventory.list._def,
			});
			queryClient.invalidateQueries({
				queryKey: queries.inventory.imports._def,
			});
		},
	});
};
